import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Editor } from '@tiptap/react';
import type { Node as PMNode } from '@tiptap/pm/model';
import { estimateRuntime } from '../../shared/runtime';
import { isSpokenAudioType } from '../../shared/audioTypes';
import {
  SPEEDS,
  formatClock,
  formatSpeed,
  planReadThrough,
  rowFacts,
  segmentAt,
  type ReadThroughPlan,
  type ReadThroughSpeed,
} from '../../shared/readThrough';
import { setReadThroughHighlight } from './Editor/ReadThroughHighlight';
import { useMediaQuery } from '../lib/useMediaQuery';

// Read-through mode (finishing bundle 2B, C12; mock-up 06 §04). Plays the
// script at speaking pace: the current row is highlighted and kept in view,
// rows with nothing spoken hold for about 2 seconds, and the player shows
// elapsed and total time. Read aloud uses the browser's own voice (free) and
// then sets the pace itself. Any scroll or tap by the reader pauses it.

const SPEED_KEY = 'hsp_readthrough_speed';
const VOICE_NOTE_KEY = 'hsp_readthrough_voice_note_seen';
/** A breath before an Interview row is read aloud. */
const INTERVIEW_PAUSE_MS = 600;
const STOP_KEYS = new Set(['PageUp', 'PageDown', 'ArrowUp', 'ArrowDown', ' ', 'Home', 'End']);

type Status = 'idle' | 'playing' | 'paused' | 'stopped' | 'finished';
type StopReason = 'scroll' | 'tap' | 'hidden';

interface PlayRow {
  pos: number;
  number: number;
  /** Spoken Audio text, as read aloud. Empty for holds. */
  text: string;
  /** Document position of each character of `text` (-1 for joins). */
  map: number[];
  interview: boolean;
}

interface Built {
  plan: ReadThroughPlan;
  /** Aligned with plan.segments. */
  rows: PlayRow[];
}

export function buildPlan(doc: PMNode): Built {
  const all: { node: PMNode; pos: number }[] = [];
  doc.forEach((node, offset) => {
    if (node.type.name === 'scriptRow') all.push({ node, pos: offset });
  });
  const plan = planReadThrough(all.map(({ node }) => rowFacts(node.toJSON())));
  const rows = plan.segments.map((segment) => {
    const { node, pos } = all[segment.rowIndex]!;
    let text = '';
    const map: number[] = [];
    let cellOffset = 0;
    node.forEach((cell, offset) => {
      if (cell.attrs.kind === 'audio' && !text && isSpokenAudioType(cell.attrs.audioType)) {
        cellOffset = offset;
        const contentStart = pos + 1 + cellOffset + 1;
        cell.descendants((child, rel) => {
          if (!child.isText || !child.text) return true;
          if (text) {
            text += ' ';
            map.push(-1);
          }
          for (let i = 0; i < child.text.length; i++) map.push(contentStart + rel + i);
          text += child.text;
          return false;
        });
      }
    });
    return {
      pos,
      number: segment.rowIndex + 1,
      text: segment.hold ? '' : text,
      map,
      interview: node.firstChild?.attrs.audioType === 'interview',
    };
  });
  return { plan, rows };
}

/** Phrase around a spoken character, bounded by punctuation. */
function phraseAround(text: string, index: number): { start: number; end: number } {
  const stops = /[,.;:!?]/;
  let start = index;
  while (start > 0 && !stops.test(text[start - 1]!)) start--;
  while (start < text.length && text[start] === ' ') start++;
  let end = index;
  while (end < text.length && !stops.test(text[end]!)) end++;
  if (end < text.length) end++;
  return { start, end };
}

function pickVoice(): SpeechSynthesisVoice | null {
  const voices = window.speechSynthesis?.getVoices() ?? [];
  return (
    voices.find((v) => v.lang === 'en-AU') ??
    voices.find((v) => v.lang.startsWith('en-AU')) ??
    voices.find((v) => v.lang.startsWith('en') && v.default) ??
    voices.find((v) => v.lang.startsWith('en')) ??
    null
  );
}

function storedSpeed(): ReadThroughSpeed {
  try {
    const value = Number(localStorage.getItem(SPEED_KEY));
    return (SPEEDS as readonly number[]).includes(value) ? (value as ReadThroughSpeed) : 1;
  } catch {
    return 1;
  }
}

interface Snapshot {
  status: Status;
  reason: StopReason | null;
  position: number;
  segIndex: number;
}

export default function ReadThrough({ editor }: { editor: Editor | null }) {
  const desktop = useMediaQuery('(min-width: 768px)');
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const speechAvailable = typeof window !== 'undefined' && 'speechSynthesis' in window;

  const [hasRuntime, setHasRuntime] = useState(false);
  const [snap, setSnap] = useState<Snapshot>({ status: 'idle', reason: null, position: 0, segIndex: 0 });
  const [speed, setSpeedState] = useState<ReadThroughSpeed>(storedSpeed);
  const [readAloud, setReadAloud] = useState(false);
  const [voiceNote, setVoiceNote] = useState(false);
  const [announce, setAnnounce] = useState('');

  const playButtonRef = useRef<HTMLButtonElement>(null);
  const playerRef = useRef<HTMLDivElement>(null);
  const engine = useRef({
    built: null as Built | null,
    status: 'idle' as Status,
    position: 0,
    segIndex: -1,
    speed: 1 as number,
    readAloud: false,
    raf: 0,
    lastTs: 0,
    rowDone: false,
    speaking: false,
    speakToken: 0,
    pauseTimer: 0,
    watchdog: 0,
    lastShown: -1,
  });

  // Same visibility rule as the badge: under 20 spoken words, no control.
  useEffect(() => {
    if (!editor) return;
    const compute = () => setHasRuntime(estimateRuntime(editor.getJSON()) !== null);
    compute();
    editor.on('update', compute);
    return () => {
      editor.off('update', compute);
    };
  }, [editor]);

  const publish = useCallback((force = false) => {
    const e = engine.current;
    // Re-render a few times a second, or once a second for reduced motion.
    const shown = reduceMotion ? Math.floor(e.position) : Math.floor(e.position * 8);
    if (!force && shown === e.lastShown) return;
    e.lastShown = shown;
    setSnap((prev) => ({ ...prev, position: e.position, segIndex: Math.max(0, e.segIndex) }));
  }, [reduceMotion]);

  const cancelSpeech = useCallback(() => {
    const e = engine.current;
    e.speakToken++;
    e.speaking = false;
    window.clearTimeout(e.pauseTimer);
    window.clearTimeout(e.watchdog);
    if (speechAvailable) window.speechSynthesis.cancel();
  }, [speechAvailable]);

  const highlight = useCallback((segIndex: number | null, phrase: { from: number; to: number } | null = null) => {
    const e = engine.current;
    if (!editor || editor.isDestroyed) return;
    if (segIndex === null || !e.built) {
      setReadThroughHighlight(editor.view, null);
      return;
    }
    const row = e.built.rows[segIndex]!;
    setReadThroughHighlight(editor.view, {
      active: true,
      rowPos: row.pos,
      rowNumber: row.number,
      rowCount: e.built.plan.segments.length,
      phrase,
    });
  }, [editor]);

  const scrollToRow = useCallback((segIndex: number) => {
    const e = engine.current;
    if (!editor || !e.built) return;
    const dom = editor.view.nodeDOM(e.built.rows[segIndex]!.pos) as HTMLElement | null;
    dom?.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
  }, [editor, reduceMotion]);

  const speakRow = useCallback((segIndex: number, immediate = false) => {
    const e = engine.current;
    const row = e.built?.rows[segIndex];
    if (!row || !row.text || !speechAvailable) {
      e.rowDone = true;
      return;
    }
    const token = ++e.speakToken;
    e.speaking = true;
    e.rowDone = false;
    const start = () => {
      if (token !== e.speakToken || e.status !== 'playing') return;
      const utterance = new SpeechSynthesisUtterance(row.text);
      const voice = pickVoice();
      if (voice) {
        utterance.voice = voice;
        utterance.lang = voice.lang;
      } else {
        utterance.lang = 'en-AU';
      }
      utterance.rate = e.speed;
      utterance.onboundary = (event) => {
        if (token !== e.speakToken || event.name === 'sentence') return;
        const { start: s, end } = phraseAround(row.text, event.charIndex);
        const from = row.map.slice(s, end).find((p) => p >= 0);
        const to = [...row.map.slice(s, end)].reverse().find((p) => p >= 0);
        if (from !== undefined && to !== undefined) highlight(segIndex, { from, to: to + 1 });
      };
      const done = () => {
        if (token !== e.speakToken) return;
        window.clearTimeout(e.watchdog);
        e.speakToken++;
        e.speaking = false;
        e.rowDone = true;
      };
      utterance.onend = done;
      utterance.onerror = done;
      const duration = e.built!.plan.segments[segIndex]!.duration / e.speed;
      e.watchdog = window.setTimeout(() => {
        if (token !== e.speakToken) return;
        done();
        window.speechSynthesis.cancel();
      }, duration * 2000 + 3000);
      window.speechSynthesis.speak(utterance);
    };
    if (row.interview && !immediate) e.pauseTimer = window.setTimeout(start, INTERVIEW_PAUSE_MS / e.speed);
    else start();
  }, [highlight, speechAvailable]);

  const enterRow = useCallback((segIndex: number, scroll = true, immediateSpeech = false) => {
    const e = engine.current;
    e.segIndex = segIndex;
    e.rowDone = false;
    highlight(segIndex);
    if (scroll) scrollToRow(segIndex);
    if (e.readAloud && !e.built!.plan.segments[segIndex]!.hold) speakRow(segIndex, immediateSpeech);
  }, [highlight, scrollToRow, speakRow]);

  const stopLoop = useCallback(() => {
    const e = engine.current;
    cancelAnimationFrame(e.raf);
    e.raf = 0;
  }, []);

  const finish = useCallback(() => {
    const e = engine.current;
    stopLoop();
    cancelSpeech();
    e.status = 'finished';
    e.position = e.built?.plan.total ?? 0;
    highlight(null);
    setSnap({ status: 'finished', reason: null, position: e.position, segIndex: Math.max(0, e.segIndex) });
    setAnnounce('Finished');
  }, [cancelSpeech, highlight, stopLoop]);

  const tick = useCallback((ts: number) => {
    const e = engine.current;
    if (e.status !== 'playing' || !e.built) return;
    const dt = e.lastTs ? Math.min(0.25, (ts - e.lastTs) / 1000) : 0;
    e.lastTs = ts;
    const segs = e.built.plan.segments;
    const seg = segs[e.segIndex];
    if (seg && e.readAloud && !seg.hold) {
      const end = seg.start + seg.duration;
      // The voice sets the pace: the clock creeps toward the row's end and
      // jumps there when the utterance finishes.
      e.position = e.rowDone ? end : Math.min(e.position + dt * e.speed, end - 0.05);
    } else {
      e.position += dt * e.speed;
    }
    if (e.position >= e.built.plan.total - 1e-6) {
      finish();
      return;
    }
    const next = segmentAt(e.built.plan, e.position + 1e-6);
    if (next !== e.segIndex) enterRow(next);
    publish();
    e.raf = requestAnimationFrame(tick);
  }, [enterRow, finish, publish]);

  const play = useCallback((fromSegment?: number) => {
    const e = engine.current;
    if (!editor) return;
    if (!e.built || e.status === 'idle' || e.status === 'finished') {
      e.built = buildPlan(editor.state.doc);
      e.position = 0;
      e.segIndex = -1;
    }
    if (e.built.plan.segments.length === 0) return;
    if (fromSegment !== undefined) {
      e.position = e.built.plan.segments[fromSegment]!.start;
      e.segIndex = -1;
    } else if (e.readAloud && e.segIndex >= 0) {
      // A voice can't resume mid-sentence reliably; restart the row.
      e.position = e.built.plan.segments[e.segIndex]!.start;
      e.segIndex = -1;
    }
    cancelSpeech();
    e.status = 'playing';
    e.lastTs = 0;
    const seg = segmentAt(e.built.plan, e.position + 1e-6);
    if (seg !== e.segIndex) enterRow(seg, true, true);
    else if (e.readAloud) speakRow(seg, true);
    setSnap({ status: 'playing', reason: null, position: e.position, segIndex: Math.max(0, e.segIndex) });
    setAnnounce('Playing');
    stopLoop();
    e.raf = requestAnimationFrame(tick);
  }, [cancelSpeech, editor, enterRow, speakRow, stopLoop, tick]);

  const pause = useCallback((reason: StopReason | null = null) => {
    const e = engine.current;
    if (e.status !== 'playing') return;
    stopLoop();
    cancelSpeech();
    e.status = reason ? 'stopped' : 'paused';
    setSnap({ status: e.status, reason, position: e.position, segIndex: Math.max(0, e.segIndex) });
    setAnnounce('Paused');
  }, [cancelSpeech, stopLoop]);

  const reset = useCallback(() => {
    const e = engine.current;
    stopLoop();
    cancelSpeech();
    e.status = 'idle';
    e.built = null;
    e.position = 0;
    e.segIndex = -1;
    highlight(null);
    setSnap({ status: 'idle', reason: null, position: 0, segIndex: 0 });
    setAnnounce('');
  }, [cancelSpeech, highlight, stopLoop]);

  const close = useCallback(() => {
    reset();
    playButtonRef.current?.focus();
  }, [reset]);

  const seek = useCallback((fraction: number) => {
    const e = engine.current;
    if (!e.built) return;
    const target = Math.max(0, Math.min(e.built.plan.total - 0.01, fraction * e.built.plan.total));
    const seg = segmentAt(e.built.plan, target);
    cancelSpeech();
    e.position = e.readAloud ? e.built.plan.segments[seg]!.start : target;
    e.segIndex = -1;
    if (e.status === 'playing') {
      enterRow(seg);
    } else {
      e.segIndex = seg;
      highlight(seg);
      scrollToRow(seg);
    }
    publish(true);
  }, [cancelSpeech, enterRow, highlight, publish, scrollToRow]);

  const changeSpeed = useCallback((next: ReadThroughSpeed) => {
    const e = engine.current;
    e.speed = next;
    setSpeedState(next);
    if (e.status === 'playing' && e.readAloud && e.segIndex >= 0 && !e.built!.plan.segments[e.segIndex]!.hold) {
      cancelSpeech();
      e.position = e.built!.plan.segments[e.segIndex]!.start;
      speakRow(e.segIndex, true);
      publish(true);
    }
  }, [cancelSpeech, speakRow, publish]);

  // Keep the engine's copies of the settings current.
  useEffect(() => {
    engine.current.speed = speed;
    try {
      localStorage.setItem(SPEED_KEY, String(speed));
    } catch {
      // Remembering the speed is a nicety.
    }
  }, [speed]);

  const toggleReadAloud = useCallback(() => {
    const e = engine.current;
    const next = !e.readAloud;
    e.readAloud = next;
    setReadAloud(next);
    if (next) {
      try {
        if (!localStorage.getItem(VOICE_NOTE_KEY)) {
          setVoiceNote(true);
          localStorage.setItem(VOICE_NOTE_KEY, '1');
        }
      } catch {
        setVoiceNote(true);
      }
    }
    if (e.status === 'playing') {
      cancelSpeech();
      const seg = e.segIndex;
      if (next && seg >= 0) {
        e.position = e.built!.plan.segments[seg]!.start;
        speakRow(seg, true);
      } else {
        e.rowDone = false;
      }
    }
  }, [cancelSpeech, speakRow]);

  // A reader's own scroll or tap pauses rather than resets. The player's own
  // scrolling never fires these (it isn't a wheel, touch or key).
  useEffect(() => {
    if (snap.status !== 'playing') return;
    const outside = (target: EventTarget | null) => {
      const el = target as HTMLElement | null;
      return !el?.closest?.('[data-rt-control]');
    };
    const onWheel = (ev: Event) => outside(ev.target) && pause('scroll');
    let touch: { x: number; y: number; started: number } | null = null;
    let tapTimer = 0;
    const onScroll = () => {
      if (!touch && !tapTimer) return;
      window.clearTimeout(tapTimer);
      tapTimer = 0;
      touch = null;
      pause('scroll');
    };
    const onTouchStart = (ev: TouchEvent) => {
      if (!outside(ev.target)) return;
      window.clearTimeout(tapTimer);
      tapTimer = 0;
      const point = ev.touches[0];
      touch = point && ev.touches.length === 1
        ? { x: point.clientX, y: point.clientY, started: performance.now() }
        : null;
    };
    const onTouchMove = (ev: TouchEvent) => {
      const point = ev.touches[0];
      if (touch && point && Math.hypot(point.clientX - touch.x, point.clientY - touch.y) > 10) onScroll();
    };
    const onTouchEnd = (ev: TouchEvent) => {
      if (!touch) return;
      const point = ev.changedTouches[0];
      if (point && Math.hypot(point.clientX - touch.x, point.clientY - touch.y) > 10) {
        onScroll();
        return;
      }
      const short = performance.now() - touch.started < 350;
      touch = null;
      // Let a scroll delivered just after touchend win over a tap.
      if (short) tapTimer = window.setTimeout(() => { tapTimer = 0; pause('tap'); }, 120);
    };
    const onTouchCancel = () => { touch = null; };
    const onKey = (ev: KeyboardEvent) => {
      if (!outside(ev.target)) return;
      if (STOP_KEYS.has(ev.key)) pause('scroll');
    };
    const onPointer = (ev: PointerEvent) => ev.pointerType !== 'touch' && outside(ev.target) && pause('tap');
    const onVisibility = () => document.visibilityState === 'hidden' && pause('hidden');
    window.addEventListener('wheel', onWheel, { passive: true });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    window.addEventListener('touchcancel', onTouchCancel, { passive: true });
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onPointer);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', onTouchCancel);
      window.removeEventListener('scroll', onScroll, true);
      window.clearTimeout(tapTimer);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [snap.status, pause]);

  // Escape closes the player and hands focus back to Play read-through.
  useEffect(() => {
    if (snap.status === 'idle') return;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape' && playerRef.current?.contains(document.activeElement)) close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [snap.status, close]);

  // Document changes and unmount reset silently. Only a reader's Close
  // action returns focus to Play, so loading a page never steals focus.
  useEffect(() => {
    reset();
    return reset;
  }, [editor, reset]);

  const built = engine.current.built;
  const total = built?.plan.total ?? 0;
  const segments = built?.plan.segments ?? [];
  const current = segments[snap.segIndex];
  const elapsedShown = reduceMotion ? Math.floor(snap.position) : snap.position;
  const fraction = total > 0 ? Math.min(1, elapsedShown / total) : 0;
  const clock = `${readAloud && snap.status === 'playing' ? 'About ' : ''}${formatClock(snap.position / speed)} of ${formatClock(total / speed)}`;
  const rowLabel = segments.length ? `Row ${built!.rows[snap.segIndex]?.number ?? 1} of ${segments.length}` : '';
  const resumeRow = built?.rows[snap.segIndex]?.number ?? 1;
  const ticks = useMemo(
    () => (total > 0 ? segments.slice(1).map((s) => (s.start / total) * 100) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [built, total],
  );

  if (!hasRuntime || !editor) return null;

  const playing = snap.status === 'playing';
  const buttonLabel = playing ? 'Pause read-through' : 'Play read-through';

  const playIcon = (
    <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" aria-hidden>
      <path d="M7 4.5v15a1 1 0 0 0 1.5.86l12.5-7.5a1 1 0 0 0 0-1.72L8.5 3.64A1 1 0 0 0 7 4.5Z" />
    </svg>
  );
  const pauseIcon = (
    <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" aria-hidden>
      <rect x="6" y="4" width="4.2" height="16" rx="1.2" />
      <rect x="13.8" y="4" width="4.2" height="16" rx="1.2" />
    </svg>
  );

  const toggle = () => (playing ? pause() : snap.status === 'stopped' ? play(snap.segIndex) : play());

  const bar = (
    <div
      className="rt-bar"
      role="slider"
      tabIndex={0}
      aria-label="Read-through position"
      aria-valuemin={0}
      aria-valuemax={Math.round(total / speed)}
      aria-valuenow={Math.round(snap.position / speed)}
      aria-valuetext={clock}
      onClick={(ev) => {
        const rect = ev.currentTarget.getBoundingClientRect();
        seek((ev.clientX - rect.left) / rect.width);
      }}
      onKeyDown={(ev) => {
        if (ev.key !== 'ArrowLeft' && ev.key !== 'ArrowRight') return;
        ev.preventDefault();
        const i = Math.max(0, Math.min(segments.length - 1, snap.segIndex + (ev.key === 'ArrowRight' ? 1 : -1)));
        seek((segments[i]!.start + 0.001) / total);
      }}
    >
      <i style={{ width: `${fraction * 100}%` }} />
      {ticks.map((left, i) => (
        <em key={i} style={{ left: `${left}%` }} />
      ))}
      <b style={{ left: `${fraction * 100}%` }} />
    </div>
  );

  const speedControl = (
    <div className="rt-seg" role="group" aria-label="Speed">
      {SPEEDS.map((s) => (
        <button
          key={s}
          type="button"
          aria-pressed={speed === s}
          className={speed === s ? 'on' : ''}
          onClick={() => changeSpeed(s)}
        >
          {formatSpeed(s)}
        </button>
      ))}
    </div>
  );

  const voiceToggle = speechAvailable && (
    <button
      type="button"
      role="switch"
      aria-checked={readAloud}
      className={`rt-switch ${readAloud ? 'on' : ''}`}
      onClick={toggleReadAloud}
    >
      <span className="rt-switch-track" aria-hidden />
      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M11 5 6 9H3v6h3l5 4Z" />
        <path d="M15.5 8.5a5 5 0 0 1 0 7M18.5 5.5a9 9 0 0 1 0 13" />
      </svg>
      Read aloud
    </button>
  );

  const closeButton = (
    <button type="button" className="rt-close" aria-label="Close read-through" onClick={close}>
      ×
    </button>
  );

  const ppButton = (
    <button
      type="button"
      className={`rt-pp ${playing || snap.status === 'paused' ? '' : 'ghost'}`}
      aria-label={playing ? 'Pause' : 'Play'}
      onClick={toggle}
    >
      {playing ? pauseIcon : playIcon}
    </button>
  );

  let body: React.ReactNode;
  if (snap.status === 'stopped' || snap.status === 'finished') {
    const message =
      snap.status === 'finished' ? (
        <>
          <b>That's the whole script.</b> {formatClock(total / speed)} at {formatSpeed(speed)}.
        </>
      ) : (
        <>
          <b>Paused at {formatClock(snap.position / speed)}.</b>{' '}
          {snap.reason === 'scroll' ? 'You scrolled, so we stopped.' : snap.reason === 'tap' ? 'You tapped the page, so we stopped.' : ''}
        </>
      );
    const action =
      snap.status === 'finished' ? (
        <button type="button" className="rt-action" onClick={() => play()}>Play again</button>
      ) : (
        <button type="button" className="rt-action" onClick={() => play(snap.segIndex)}>
          Resume from row {resumeRow}
        </button>
      );
    body = desktop ? (
      <>
        {ppButton}
        <span className="rt-msg">{message}</span>
        {action}
        {closeButton}
      </>
    ) : (
      <>
        <div className="rt-r1">
          {ppButton}
          <span className="rt-msg">{message}</span>
          {closeButton}
        </div>
        <div className="rt-r2">{action}</div>
      </>
    );
  } else {
    const time = (
      <span className="rt-time">
        {clock}
        <small>{rowLabel}</small>
      </span>
    );
    const hold = current?.hold && playing && <span className="rt-hold">Visual only · 2 sec</span>;
    body = desktop ? (
      <>
        {ppButton}
        {time}
        {hold}
        {bar}
        {speedControl}
        {voiceToggle}
        {closeButton}
      </>
    ) : (
      <>
        <div className="rt-r1">
          {ppButton}
          {time}
          {hold}
          {closeButton}
        </div>
        {bar}
        <div className="rt-r2">
          {speedControl}
          {voiceToggle}
        </div>
      </>
    );
  }

  return (
    <>
      <button
        ref={playButtonRef}
        type="button"
        data-rt-control
        data-print-hide
        className={`rt-play ${snap.status !== 'idle' ? 'on' : ''}`}
        aria-label={buttonLabel}
        onClick={toggle}
      >
        {playing ? pauseIcon : playIcon}
        <span className="hidden md:inline">{buttonLabel}</span>
        <span className="md:hidden">{playing ? 'Pause' : 'Play'}</span>
      </button>
      <span className="sr-only" aria-live="polite">{announce}</span>
      {snap.status !== 'idle' &&
        createPortal(
          <div
            ref={playerRef}
            data-rt-control
            data-print-hide
            role="toolbar"
            aria-label="Read-through"
            className={`rt-player ${desktop ? 'rt-player-desk' : 'rt-player-phone'}`}
          >
            {body}
            {voiceNote && readAloud && (
              <p className="rt-note">Uses your device's own voice. Nothing is sent anywhere.</p>
            )}
          </div>,
          document.body,
        )}
    </>
  );
}
